import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { useEffect, useRef, useState } from "react";
import { BookingsSectionTitle } from "./styles";
import PersonalBasicInformation from "./PersonalInformationDetails/PersonalBasicInformation";
import AliasesList from "./PersonalInformationDetails/AliasesList";
import AddressesList from "./PersonalInformationDetails/AddressesList";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import DynamicForm from "@/common/DynamicForm";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import FolderDetailSearchModal from "@/components/FolderDetailSearch/FolderDetailSearchModal";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import FieldVerificationContext, {
  useFieldVerificationState,
} from "@/contexts/FieldVerificationContext";
import VerificationHint from "@/common/VerificationHint";
import notificationService from "@/util/notificationService";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const MOVEMENT_RULES_HEADERS = [
  { key: "summaryDescription", text: "Summary Description" },
  { key: "currentLocation", text: "Current Location" },
];

const BookingsMovementRules = () => {
  const [movementRuleForm] = Form.useForm();
  const dispatch = useDispatch();
  const verification = useFieldVerificationState("InmateMovementRules");
  const inmateMovementRuleList = useSelector(
    (state) => state.bookings?.bookings?.inmateMovementRule
  );
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: movementRulesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:InmateMovementRules", MOVEMENT_RULES_HEADERS);
  const [isFolderSearchModal, setIsFolderSearchModal] = useState(false);
  const [ruleList, setRuleList] = useState(inmateMovementRuleList || []);
  const [selectedRow, setSelectedRow] = useState();
  const [showModal, setShowModal] = useState(false);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [ruleType, setRuleType] = useState(null);
  const [keepSeparate, setKeepSeparate] = useState(null);
  const [formData, setFormData] = useState();
  const [isEdit, setIsEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [inmateMovementRuleID, setInmateMovementRuleID] = useState(null);
  const [tableData, setTableData] = useState({
    rows: [],
  });

  const [selectedAliases, setSelectedAliases] = useState(
    selectedRow?.jackets?.[0]?.bio?.[0]?.bioAliases || []
  );
  const [selectedAddresses, setSelectedAddresses] = useState(
    selectedRow?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
  );

  const fullSearch = useSelector(state => state.fullSearch);

  // -----------------------------------------------------------------------
  // InmateMovementRulesExtra — PCI's per-client dynamic "_U" columns for
  // this rule (InmateMovementRulesInterface.asp's "Additional Rule
  // Information" section, shown right after "Other Information").
  // -----------------------------------------------------------------------
  const [movementRuleExtraFields, setMovementRuleExtraFields] = useState([]);
  const [movementRuleExtraFieldsReady, setMovementRuleExtraFieldsReady] =
    useState(false);
  const movementRuleExtraTypeMapRef = useRef({});
  const movementRuleExtraIdRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/InmateMovementRulesExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(
          tableData,
          "InmateMovementRulesExtra"
        );
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        movementRuleExtraTypeMapRef.current = typeMap;
        setMovementRuleExtraFields(converted);
        setMovementRuleExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[BookingsMovementRules] InmateMovementRulesExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  // Fetches the saved InmateMovementRulesExtra row when editing an existing
  // rule — a brand new rule has no inmateMovementRuleID yet.
  useEffect(() => {
    if (!movementRuleExtraFieldsReady) return;
    const id = selectedRow?.inmateMovementRuleID;
    if (!isNonEmptyVariable(id)) {
      movementRuleExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("InmateMovementRulesExtra", "inmateMovementRuleID", id)
      .then((result) => {
        if (!result) return;
        movementRuleExtraIdRef.current =
          result.raw?.InmateMovementRuleExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = movementRuleExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        movementRuleForm.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error(
          "[BookingsMovementRules] InmateMovementRulesExtra fetch failed:",
          error
        );
      });
  }, [selectedRow?.inmateMovementRuleID, movementRuleExtraFieldsReady]);

  useEffect(() => {
    buildTableData(ruleList);
    updateMovementRule();
  }, [ruleList]);

  useEffect(() => {
    setSelectedAliases(selectedRow?.jackets?.[0]?.bio?.[0]?.bioAliases || []);
    setSelectedAddresses(
      selectedRow?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
    );
    movementRuleForm.setFieldsValue(selectedRow);
  }, [selectedRow]);

  useEffect(() => {
    const personalData =
      movementRuleForm.getFieldsValue([
        "lName",
        "fName",
        "mName",
        "suffix",
        "daytimePhone",
        "phone",
        "dob",
        "dlExpiration",
      ]) || {};
    const jacketsPayload = [
      {
        bio: [
          {
            ...personalData,
            bioAliases: selectedAliases,
            bioAddresses: selectedAddresses,
          },
        ],
      },
    ];
    movementRuleForm.setFieldsValue({ jackets: jacketsPayload });
  }, [selectedAliases, selectedAddresses, movementRuleForm]);

  const handleModal = (val) => {
    if (val == "edit") {
      setIsEdit(true);
    }
    setShowModal(!showModal);
    if (selectedRow) {
      setKeepSeparate(selectedRow?.keepSeparateTypeID || null);
    } else {
      setKeepSeparate(null);
    }
    setIsRowSelected(false);
  };

  const handleRuleTypeChange = (value) => {
    setRuleType(value);
    movementRuleForm.setFieldValue("inmateMovementCommonID", null);
    movementRuleForm.setFieldValue("keepSeparateTypeID", null);
  };

  const keepSeparateChange = (value) => {
    setKeepSeparate(value);
    if (value === 54030002) {
      setIsFolderSearchModal(true);
    } else {
      setIsFolderSearchModal(false);
    }
  };

  const buildTableData = (rules) => {
    const tableRowList = [];
    rules?.forEach((item) => {
      const list = { ...item };
      if (item?.inmateMovementCommonID) {
        list["summaryDescription"] = `${
          getOptionsFromRefList(
            "Inmate_Movement_Common",
            false,
            false,
            true
          ).filter((data) => item?.inmateMovementCommonID === data?.value)?.[0]
            ?.label
        } ${item?.comments?.trim() ? item?.comments?.trim() : ""}`;
      } else if (
        item?.keepSeparateTypeID ||
        item?.inmateMovementRuleTypeID === 54020001
      ) {
        list["summaryDescription"] = item?.keepSeparateTypeID
          ? `Keep Separate from ${
              getOptionsFromRefList(
                "Keep_Separate_Type",
                false,
                false,
                true
              ).filter((data) => item?.keepSeparateTypeID === data?.value)?.[0]
                ?.label
            }. ${item?.comments?.trim() ? item?.comments?.trim() : ""}`
          : `Keep Separate from. ${
              item?.comments?.trim() ? item?.comments?.trim() : ""
            }`;
      } else {
        list["summaryDescription"] = item?.comments;
      }
      list["currentLocation"] = "";
      list["movementRulId"] = item?.inmateMovementRuleID || item?.id;
      tableRowList.push(list);
    });
    setTableData((prev) => ({ ...prev, rows: tableRowList }));
  };

  const addRuleType = () => {
    verification.clearAll();
    setKeepSeparate(null);
    setSelectedRow({
      expirationDate: null,
      inmateMovementRuleTypeID: null,
      inmateMovementCommonID: null,
      keepSeparateTypeID: null,
      comments: null,
    });
    setInmateMovementRuleID(null);
    handleModal();
    setRuleType(null);
  };

  const patchSelectedRow = (data) => {
    verification.clearAll();
    setInmateMovementRuleID(data?.inmateMovementRuleID || data?.id);
    const rowData = ruleList?.filter(
      (item) =>
        item?.inmateMovementRuleID === data?.movementRulId ||
        item?.id === data?.movementRulId
    )?.[0];
    setSelectedRow({
      ...rowData,
      expirationDate: rowData?.expirationDate
        ? dayjs(rowData?.expirationDate)
        : null,
    });
    setIsRowSelected(true);
    setRuleType(rowData?.inmateMovementRuleTypeID);
  };

  const deleteMovementRule = () => {
    const tempArray = ruleList?.filter(
      (item) =>
        (item?.inmateMovementRuleID &&
          item?.inmateMovementRuleID !== inmateMovementRuleID) ||
        (item?.id && item?.id !== inmateMovementRuleID)
    );
    setRuleList([...tempArray]);
    const url = API_ENDPOINTS.movementRule + "/id/" + inmateMovementRuleID;
    dispatch(setDeleteAPis(url));
    setSelectedRow(null);
    setIsRowSelected(false);
  };

  const updateRowData = (oldData, newData, extra) => {
    const existingExtra = oldData?.inmateMovementRulesExtra?.[0] || {};
    return {
      ...oldData,
      expirationDate: newData?.expirationDate
        ? dayjs(newData?.expirationDate).format("MM/DD/YYYY")
        : null,
      inmateMovementRuleTypeID: newData?.inmateMovementRuleTypeID,
      inmateMovementCommonID: newData?.inmateMovementCommonID,
      keepSeparateTypeID: newData?.keepSeparateTypeID,
      comments: newData?.comments,
      jackets: newData?.jackets,
      // InmateMovementRulesExtra is nested here rather than sent embedded
      // in the main bookings save payload — see pages/Bookings/index.jsx's
      // strip-before-POST and saveInmateMovementRulesExtra, same spot
      // HoldsExtra/BookingBailsExtra stash theirs.
      inmateMovementRulesExtra:
        extra?.fieldKeys?.length > 0
          ? [
              {
                ...existingExtra,
                ...extra.data,
                inmateMovementRuleExtraID:
                  existingExtra?.inmateMovementRuleExtraID ??
                  extra?.extraID ??
                  0,
                inmateMovementRuleID:
                  existingExtra?.inmateMovementRuleID ??
                  oldData?.inmateMovementRuleID ??
                  0,
              },
            ]
          : oldData?.inmateMovementRulesExtra,
    };
  };

  const saveInmateRule = () => {
    const personalData =
      movementRuleForm.getFieldsValue([
        "lName",
        "fName",
        "mName",
        "suffix",
        "daytimePhone",
        "phone",
        "dob",
        "dlExpiration",
      ]) || {};
    const jacketsPayload = [
      {
        bio: [
          {
            ...personalData,
            bioAliases: selectedAliases,
            bioAddresses: selectedAddresses,
          },
        ],
      },
    ];
    movementRuleForm.setFieldsValue({ jackets: jacketsPayload });

    if (verification.hasUnverifiedFields()) {
      notificationService.error(
        "Please verify all fields copied from the selected record before saving."
      );
      return;
    }

    movementRuleForm
      .validateFields()
      .then((values) => {
        // Split InmateMovementRulesExtra dynamic fields out of the form
        // values — nested onto inmateMovementRulesExtra[0] by updateRowData
        // below rather than left in values, where they'd otherwise leak
        // into the main bookings save payload.
        const movementRuleExtraFieldKeys = movementRuleExtraFields.map(
          (f) => f.columnName
        );
        const movementRuleExtraData = {};
        movementRuleExtraFieldKeys.forEach((k) => {
          const val = values?.[k];
          const htmlType = movementRuleExtraTypeMapRef.current[k];
          movementRuleExtraData[k] = val
            ? htmlType === "date"
              ? dayjs(val).format("MM/DD/YYYY")
              : htmlType === "datetime"
                ? dayjs(val).format("MM/DD/YYYY HH:mm")
                : val
            : null;
          delete values[k];
        });
        const movementRuleExtra = {
          fieldKeys: movementRuleExtraFieldKeys,
          data: movementRuleExtraData,
          extraID: movementRuleExtraIdRef.current,
        };

        if (inmateMovementRuleID) {
          setRuleList((prev) =>
            prev.map((p) =>
              p?.inmateMovementRuleID === inmateMovementRuleID ||
              p?.id === inmateMovementRuleID
                ? updateRowData(p, values, movementRuleExtra)
                : p
            )
          );
        } else {
          setRuleList((prev) => [
            ...prev,
            {
              ...values,
              id: uuidv4(),
              expirationDate: values?.expirationDate
                ? dayjs(values?.expirationDate).format("MM/DD/YYYY")
                : null,
              inmateMovementRulesExtra:
                movementRuleExtraFieldKeys.length > 0
                  ? [
                      {
                        ...movementRuleExtraData,
                        inmateMovementRuleExtraID: 0,
                        inmateMovementRuleID: 0,
                      },
                    ]
                  : undefined,
            },
          ]);
        }
        handleModal();
      })
      .catch((err) => console.log(err));
  };

  const updateMovementRule = () => {
    dispatch(
      setBookings({
        inmateMovementRule: [...ruleList],
      })
    );
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.trim() === "") {
      movementRuleForm.setFieldValue("comments", null);
    }
  };

  // Also fires for FolderDetailSearchModal's "Create New Folder" button
  // (same onSaveClick callback) — meta.isNewJacket tells the two apart.
  const handleSelectCurrentJacket = (selectedFolder, meta) => {
    if (!selectedFolder) {
      alert("Please select a folder from the list first.");
      return;
    }
    const folderData = selectedFolder.originalData;
    const jacket = folderData?.jackets
      ? folderData?.jackets
      : { staffJacket: false, orphanFlag: false, bio: [selectedFolder] };
    const nonPersonalKeys = [
      "inmateMovementRuleTypeID",
      "inmateMovementCommonID",
      "keepSeparateTypeID",
      "comments",
      "expirationDate",
      "jackets",
    ];
    const currentNonPersonal = movementRuleForm.getFieldsValue(nonPersonalKeys);

    const updatedRow = {
      ...currentNonPersonal,
      keepSeparateTypeID: 54030002,
      jackets: [jacket],
      // Distinguishes this from patchSelectedRow's "edit an existing rule"
      // path — only a fresh EXISTING jacket pick should require field
      // verification. A brand-new folder (meta.isNewJacket) has nothing to
      // verify against.
      isFreshJacketPick: !meta?.isNewJacket,
    };

    setSelectedRow(updatedRow);
    movementRuleForm.setFieldsValue(updatedRow);
    setIsFolderSearchModal(false);
  };
  useEffect(() => {
    if (isEdit) {
      let isShow = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      );
      if (isShow) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEdit]);
  return (
    <FieldVerificationContext.Provider value={verification}>
      <StyledGridRow
        gutter={16}
        justify="space-between"
        style={{ marginBottom: "15px" }}
      >
        <StyledGridCol>
          <Button
            color="primary"
            type="primary"
            onClick={addRuleType}
            disabled={
              !bookingsFieldsEnabled ||
              isConfiguring ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.ADD,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              )
            }
          >
            Add Movement Rules
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button
            color="primary"
            disabled={
              !isRowSelected ||
              isConfiguring ||
              tableData?.rows?.length === 0 ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.VIEW,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              )
            }
            type="primary"
            onClick={() => handleModal("edit")}
          >
            Details
          </Button>
          <Popconfirm
            title="Delete the Movement Rules"
            description="Are you sure to delete this Movement Rule?"
            onConfirm={deleteMovementRule}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button
              color="primary"
              type="primary"
              style={{ marginLeft: "10px" }}
              disabled={
                !bookingsFieldsEnabled ||
                !isRowSelected ||
                isConfiguring ||
                tableData?.rows?.length === 0 ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.DELETE,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Delete
            </Button>
          </Popconfirm>

          {/* Config — drag-reorder/resize columns; only while Modify is active */}
          {!isConfiguring ? (
            <Button
              color="primary"
              type="primary"
              style={{ marginLeft: "10px" }}
              htmlType="button"
              onClick={startConfiguring}
            >
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Do you want to save the current list view configuration?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                style={{ marginLeft: "10px" }}
                htmlType="button"
              >
                {configButtonLabel}
              </Button>
            </Popconfirm>
          )}
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          ref={dataTableRef}
          headers={movementRulesHeaders}
          draggable={isConfiguring}
          data={tableData?.rows}
          onRowClick={(data) => patchSelectedRow(data)}
          onRowDoubleClick={(data) => {
            patchSelectedRow(data);
            handleModal("edit");
          }}
          selected={selectedRow === null}
        />
      )}

      <Modal
        header="Inmate Movement Rule"
        size="xl"
        show={showModal}
        onHide={() => {
          verification.clearAll();
          setShowModal(false);
          setRuleType(null);
          setKeepSeparate(null);
          setSelectedRow(null);
          setIsEdit(false);
        }}
        footerActions={[
          {
            key: "save",
            text: "Save",
            onClick: saveInmateRule,
            disabled: !bookingsFieldsEnabled || isDisabled,
          },
        ]}
        footerCloseAction={() => setIsEdit(false)}
        footerCloseActionText={"Cancel"}
      >
        <Form
          form={movementRuleForm}
          initialValues={formData}
          disabled={!bookingsFieldsEnabled}
        >
          <StyledGridRow gutter={16}>
            <StyledGridCol span={6}>
              <FormItem
                label="Rule Type"
                name="inmateMovementRuleTypeID"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Rule Type is required",
                    },
                  ] : []
                }
              >
                <Select
                  options={getOptionsFromRefList(
                    "Inmate_Movement_Rule_Type",
                    false,
                    false,
                    true
                  )}
                  placeholder="Rule Type"
                  showSearch={true}
                  variant="outlined"
                  onChange={(e) => {
                    // setTimeout(() => document.activeElement.blur(), 0);
                    handleRuleTypeChange(e);
                  }}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <FormItem label="Expiration Date" name="expirationDate">
                <CommonDatePicker showTime={false} format="MM/DD/YYYY" disabled={!bookingsFieldsEnabled} />
              </FormItem>
            </StyledGridCol>
            {ruleType === 54020003 && (
              <StyledGridCol span={6}>
                <FormItem label="Common Rules" name="inmateMovementCommonID">
                  <Select
                    options={getOptionsFromRefList(
                      "Inmate_Movement_Common",
                      false,
                      false,
                      true
                    )}
                    placeholder="Common Rules"
                    showSearch={true}
                    variant="outlined"
                    disabled={!bookingsFieldsEnabled}
                    // onChange={() =>
                    //   setTimeout(() => document.activeElement.blur(), 0)
                    // }
                  />
                </FormItem>
              </StyledGridCol>
            )}
            {ruleType === 54020001 && (
              <StyledGridCol span={6}>
                <FormItem label="Keep Separate From" name="keepSeparateTypeID">
                  <Select
                    options={getOptionsFromRefList(
                      "Keep_Separate_Type",
                      false,
                      false,
                      true
                    )}
                    placeholder="Keep Separate From"
                    showSearch={true}
                    variant="outlined"
                    onChange={(e) => {
                      // setTimeout(() => document.activeElement.blur(), 0);
                      keepSeparateChange(e);
                    }}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>

          {keepSeparate === 54030002 && (
            <StyledGridRow>
              <StyledGridCol span={24}>
                <BookingsSectionTitle>
                  <span>Personal Information</span>
                </BookingsSectionTitle>
                <VerificationHint />
                <PersonalBasicInformation
                  selectedAssociate={selectedRow?.jackets?.[0]?.bio?.[0]}
                  fromJacketSearch={!!selectedRow?.isFreshJacketPick}
                  form={movementRuleForm}
                  associates={true}
                />
                <AliasesList
                  showQuickAddForm={false}
                  selectedAliases={selectedAliases}
                  associates={true}
                  onAliasesChange={setSelectedAliases}
                />
                <FormItem name="bioAliases" hidden>
                  <TextField />
                </FormItem>
                <AddressesList
                  selectedAddresses={selectedAddresses}
                  associates={true}
                  onAddressesChange={setSelectedAddresses}
                />
                <FormItem name="bioAddresses" hidden>
                  <TextField />
                </FormItem>
                <FormItem name="jackets" hidden>
                  <TextField />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
          )}
          <StyledGridRow>
            <StyledGridCol span={18}>
              <BookingsSectionTitle>
                <span>Other Information</span>
              </BookingsSectionTitle>
              <FormItem
                name="comments"
                onChange={handleChange}
                rules={[
                  {
                    required:
                      ruleType === 54020002 || keepSeparate === 54030003,
                    message: "Other Information is required",
                  },
                ]}
              >
                <TextField
                  inputType="textarea"
                  rows="3"
                  onChange={(e) => {
                    if (e.target.value.trim()) {
                      return false;
                    } else {
                      return true;
                    }
                  }}
                />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          {movementRuleExtraFields.length > 0 && (
            <>
              <BookingsSectionTitle>
                <span>Additional Rule Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                {movementRuleExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={movementRuleForm}
                    bookingsFieldsEnabled={bookingsFieldsEnabled}
                  />
                ))}
              </StyledGridRow>
            </>
          )}
        </Form>
      </Modal>

      {isFolderSearchModal && (
        <FolderDetailSearchModal
          show={isFolderSearchModal}
          onHide={() => setIsFolderSearchModal(false)}
          onSaveClick={handleSelectCurrentJacket}
        />
      )}
    </FieldVerificationContext.Provider>
  );
};

export default BookingsMovementRules;
